import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { CLAIM_MINE } from '../../util_components/instant_alert/claim';
import type { ClaimState } from '../../util_components/instant_alert/claim';
import { timer_color_token_css as timerColorTokenCss } from '../../util_components/instant_alert/instant_timer';
import {
  PHASE_COLOR,
  claim_remaining_ms,
  format_hold,
  hold_forces_read_only,
  hold_phase
} from './claim_hold';
import type { HoldPhase } from './claim_hold';

interface Props {
  claim: ClaimState;
  now_ms: number;
  on_release: () => void;
  on_reopen: () => void;
  releasing?: boolean;
  assign_slot?: ReactNode;
}

const PHASE_SURFACE: Record<HoldPhase, CSSProperties> = {
  ok: { background: 'var(--surface-sunken)', borderColor: 'var(--border-default)' },
  warn: { background: 'var(--state-warning-bg)', borderColor: 'var(--state-warning)' },
  critical: { background: 'var(--state-error-bg)', borderColor: 'var(--state-error)' },
  expired: { background: 'var(--state-error-bg)', borderColor: 'var(--state-error)' }
};

const BUTTON: CSSProperties = {
  margin: 0,
  background: 'var(--surface-card)',
  borderColor: 'var(--border-default)',
  color: 'var(--text-primary)'
};

function ClaimCountdownBanner({
  claim,
  now_ms,
  on_release,
  on_reopen,
  releasing = false,
  assign_slot = null
}: Props) {
  if (hold_forces_read_only(claim, now_ms)) {
    return (
      <div
        role="status"
        data-testid="claim-hold-banner"
        className="mb-4 flex flex-wrap items-center gap-3 rounded-md border px-4 py-3"
        style={{ ...PHASE_SURFACE.expired, maxWidth: 720 }}
      >
        <span className="text-sm font-semibold" style={{ color: 'var(--state-error-text)' }}>
          Returned to pool — reopen to claim
        </span>
        <button
          type="button"
          onClick={on_reopen}
          className="ml-auto rounded-md border px-3 py-1 text-xs font-medium"
          style={BUTTON}
        >
          Reopen
        </button>
      </div>
    );
  }
  if (claim.state !== CLAIM_MINE) return null;

  const remaining = claim_remaining_ms(claim, now_ms);
  const timed = Number.isFinite(remaining);
  const phase = hold_phase(remaining);
  return (
    <div
      role="status"
      data-testid="claim-hold-banner"
      data-phase={timed ? phase : 'untimed'}
      className="mb-4 flex flex-wrap items-center gap-3 rounded-md border px-4 py-3"
      style={{ ...PHASE_SURFACE[timed ? phase : 'ok'], maxWidth: 720 }}
    >
      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
        <span className="font-semibold">Held by you</span>
        {timed ? (
          <>
            {' · returns to pool in '}
            <span
              style={{
                color: timerColorTokenCss(PHASE_COLOR[phase]),
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {format_hold(remaining)}
            </span>
            {' without activity (call, message or outcome)'}
          </>
        ) : null}
      </span>
      <span className="ml-auto flex items-center gap-2">
        {assign_slot}
        <button
          type="button"
          onClick={on_release}
          disabled={releasing}
          className="rounded-md border px-3 py-1 text-xs font-medium disabled:opacity-50"
          style={BUTTON}
        >
          {releasing ? 'Releasing…' : 'Release'}
        </button>
      </span>
    </div>
  );
}

export default ClaimCountdownBanner;
